import { Vehicle } from 'core/models/vehicle';
import { DriveCommand } from 'core/models/drive';
import { Project } from 'core/models/project';
import { Environment } from 'core/models/environment';
import { RTCCommandAck, RTCTask } from 'core/models/rtc';
import { StableWebSocket } from 'infrastructure/utility/StableWebSocket';

export class VehicleStatusError extends Error {
}

export class VehiclePostCommandError extends VehicleStatusError {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    constructor(reason: any) {
        super(`cannot post a command because: ${reason}`);
    }
}

export class VehicleStatusConnectionError extends VehicleStatusError {
    constructor(vehicleId: Vehicle.Id) {
        super(`cannot connect to the server to sense the status of vehicle ${vehicleId}`);
    }
}

export type VehicleStatusListener = (status: Vehicle.Status) => void;
export type VehicleStatusErrorListener = (error: VehicleStatusError) => void;
export type RTCRequestListener = (tasks: RTCTask[]) => void;
export type RTCResponseListener = (responses: RTCCommandAck[], transactionId: string | undefined) => void;

export interface VehicleRepository {
    getVehicles(projectId: Project.Id, environmentId: Environment.Id, options?: { signal?: AbortSignal }): Promise<Vehicle[]>;
    getVehicle(id: Vehicle.Id, projectId: Project.Id, environmentId: Environment.Id): Promise<Vehicle>;

    addVehicleStatusListener(vehicleId: Vehicle.Id, projectId: Project.Id, listener: VehicleStatusListener): void;
    removeVehicleStatusListener(vehicleId: Vehicle.Id, projectId: Project.Id, listener: VehicleStatusListener): void;

    addVehicleStatusErrorListener(listener: VehicleStatusErrorListener): void;
    removeVehicleStatusErrorListener(listener: VehicleStatusErrorListener): void;

    postDriveCommand(vehicleId: Vehicle.Id, projectId: Project.Id, environmentId: Environment.Id, command: Partial<DriveCommand>): Promise<void>;
    cancelPostDriveCommand(vehicle: Vehicle.Id): Promise<void>;

    postHeartbeat(vehicleId: Vehicle.Id, projectId: Project.Id): void;
    addVehicleHeartbeatListener(vehicleId: Vehicle.Id, projectId: Project.Id, listener: (timestamp: number, accept: boolean) => void): void;
    removeVehicleHeartbeatListener(vehicleId: Vehicle.Id, projectId: Project.Id, listener: (timestamp: number, accept: boolean) => void): void;

    addRTCRequestListener(vehicleId: Vehicle.Id, projectId: Project.Id, listener: RTCRequestListener): void;
    removeRTCRequestListener(vehicleId: Vehicle.Id, projectId: Project.Id, listener: RTCRequestListener): void;

    addRTCCommandResponseListener(vehicleId: Vehicle.Id, projectId: Project.Id, listener: RTCResponseListener): void;
    removeRTCCommandResponseListener(vehicleId: Vehicle.Id, projectId: Project.Id, listener: RTCResponseListener): void;

    postRTCCommand(vehicleId: Vehicle.Id, projectId: Project.Id, environmentId: Environment.Id, task: RTCTask, activated: boolean, transactionId: string): Promise<void>;

    getWebSocket?(): StableWebSocket | undefined;
}
